import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Face Facts — FTUE demo (3 essais).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
const STATIC_FACES = [
    { id: 'sophie', avatar: '👩‍🦱', name: 'Sophie', job: 'architecte' },
    { id: 'leo', avatar: '🧑‍🦰', name: 'Léo', job: 'cuisinier' },
    { id: 'anna', avatar: '👩', name: 'Anna' },
    { id: 'pablo', avatar: '👨', name: 'Pablo' },
    { id: 'ines', avatar: '👩‍🦰', name: 'Inès' },
    { id: 'mateo', avatar: '🧑‍🦲', name: 'Mateo' },
];
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'face_facts.demo.step1_title',
        hintKey: 'face_facts.demo.step1_hint',
        faces: [STATIC_FACES[0]],
        targetIdx: 0,
        candidatesIds: ['sophie', 'leo', 'anna'],
    },
    {
        idx: 2,
        titleKey: 'face_facts.demo.step2_title',
        hintKey: 'face_facts.demo.step2_hint',
        faces: [STATIC_FACES[0], STATIC_FACES[1]],
        targetIdx: 1,
        candidatesIds: ['sophie', 'leo', 'pablo'],
    },
    {
        idx: 3,
        titleKey: 'face_facts.demo.step3_title',
        hintKey: 'face_facts.demo.step3_hint',
        faces: [STATIC_FACES[0], STATIC_FACES[1], STATIC_FACES[2]],
        targetIdx: 2,
        candidatesIds: ['sophie', 'anna', 'mateo'],
    },
];
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('encoding');
    const [encodingIdx, setEncodingIdx] = useState(0);
    const [pickedId, setPickedId] = useState(null);
    const timerRef = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const target = step.faces[step.targetIdx];
    const candidates = step.candidatesIds.map((id) => STATIC_FACES.find((f) => f.id === id));
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const correct = pickedId === target.id;
    useEffect(() => {
        setPhase('encoding');
        setEncodingIdx(0);
        setPickedId(null);
    }, [stepIdx]);
    useEffect(() => {
        if (phase !== 'encoding')
            return;
        timerRef.current = setTimeout(() => {
            if (encodingIdx + 1 >= step.faces.length) {
                setPhase('test');
            }
            else {
                setEncodingIdx((i) => i + 1);
            }
        }, 2500);
        return () => {
            if (timerRef.current)
                clearTimeout(timerRef.current);
        };
    }, [phase, encodingIdx, step.faces.length]);
    const handlePick = (id) => {
        if (phase !== 'test')
            return;
        setPickedId(id);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('face_facts.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), phase === 'encoding' && (_jsxs("div", { className: "flex flex-col items-center gap-s3 border border-cyan-border-faint bg-cyan-surface p-s5", children: [_jsx("span", { className: "text-[64px]", children: step.faces[encodingIdx].avatar }), _jsx("span", { className: "font-display text-[20px] text-cyan-text", children: step.faces[encodingIdx].name }), step.faces[encodingIdx].job && (_jsx("span", { className: "font-mono text-[10px] uppercase tracking-[0.20em] text-cyan-text-faint", children: step.faces[encodingIdx].job })), _jsxs("span", { className: "font-mono text-[10px] text-cyan-text-secondary", children: [encodingIdx + 1, "/", step.faces.length] })] })), (phase === 'test' || phase === 'feedback') && (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-[13px] text-cyan-text-secondary", children: t('face_facts.demo.question', { name: target.name, job: target.job ?? '' }) }), _jsx("div", { className: "flex gap-s3", children: candidates.map((c) => {
                            const isTarget = c.id === target.id;
                            const isPicked = pickedId === c.id;
                            const isFb = phase === 'feedback';
                            const cls = isFb
                                ? isTarget
                                    ? 'border border-cyan-core bg-cyan-core/10'
                                    : isPicked
                                        ? 'border border-cyan-media bg-cyan-media/10'
                                        : 'border border-cyan-border-faint bg-cyan-surface opacity-50'
                                : 'border border-cyan-border bg-cyan-surface hover:bg-cyan-border-faint';
                            return (_jsx("button", { type: "button", onClick: () => handlePick(c.id), disabled: phase !== 'test', className: ['flex h-[80px] w-[80px] items-center justify-center text-[40px] transition', cls].join(' '), children: c.avatar }, c.id));
                        }) })] })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'feedback' && (_jsxs(_Fragment, { children: [_jsx("p", { className: [
                                    'text-[13px]',
                                    correct ? 'text-cyan-core' : 'text-cyan-media',
                                ].join(' '), children: correct
                                    ? t('face_facts.demo.feedback_correct', { name: target.name })
                                    : t('face_facts.demo.feedback_incorrect', { name: target.name }) }), _jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })] })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map